import type { Account } from "./AccountContext";
import { isCdhIdIamIdMigrationEnabled } from "@/store/dxpIamAuthHeader";

export type DealerAccountRouteIds = Pick<Account, "id" | "iamId">;

/** Account id segment used in in-app URLs (`/:accountId/...`). */
export function getDealerAccountRouteId(
  account: DealerAccountRouteIds
): string {
  return isCdhIdIamIdMigrationEnabled() ? account.iamId : account.id;
}

export function findDealerAccountByRouteSegment(
  accounts: readonly Account[],
  routeSegment: string
): Account | undefined {
  if (!isCdhIdIamIdMigrationEnabled()) {
    return accounts.find((account) => account.id === routeSegment);
  }

  return (
    accounts.find((account) => account.iamId === routeSegment) ??
    accounts.find((account) => account.id === routeSegment)
  );
}

/** Rewrites legacy CDH account segments in paths when IAM routing is enabled. */
export function canonicalizeDealerAccountPath(
  pathname: string,
  accounts: readonly Account[]
): string {
  if (!isCdhIdIamIdMigrationEnabled()) {
    return pathname;
  }

  const match = /^\/([^/]+)(\/.*)?$/.exec(pathname);
  if (match === null) {
    return pathname;
  }

  const segment = match[1];
  const suffix = match[2] ?? "";

  if (accounts.some((account) => account.iamId === segment)) {
    return pathname;
  }

  const accountByCdh = accounts.find((account) => account.id === segment);
  if (accountByCdh === undefined) {
    return pathname;
  }

  return `/${getDealerAccountRouteId(accountByCdh)}${suffix}`;
}
